import { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

/**
 * @param {number} cost
 * @returns {string}
 */
function formatOptionCost(cost) {
  if (cost === 0) return 'Free';
  return `${cost} ${cost === 1 ? 'credit' : 'credits'}`;
}

function normalizeOptions(options) {
  return options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );
}

export default function NewDesignSelect({
  value,
  onChange,
  options,
  id: idProp,
  className = '',
  'aria-labelledby': ariaLabelledBy,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const autoListId = useId();
  const autoTriggerId = useId();
  const listId = `${autoListId}-list`;
  const triggerId = idProp || autoTriggerId;

  const normalized = normalizeOptions(options);
  const selected =
    normalized.find((opt) => opt.value === value) ?? normalized[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDocumentMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [open]);

  const triggerClassName = ['new-design-select-trigger', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={rootRef}
      className={`new-design-select${open ? ' new-design-select--open' : ''}`}
    >
      <button
        type="button"
        id={triggerId}
        className={triggerClassName}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((wasOpen) => !wasOpen);
        }}
      >
        <span className="new-design-select-trigger-label">
          {selected?.label ?? ''}
        </span>
        <span className="new-design-select-trigger-meta">
          {selected?.cost != null ? (
            <span className="new-design-select-option-cost">
              {formatOptionCost(selected.cost)}
            </span>
          ) : null}
          <ChevronDown
            className="new-design-select-chevron"
            size={14}
            strokeWidth={2.5}
            aria-hidden
          />
        </span>
      </button>
      {open ? (
        <ul
          id={listId}
          role="listbox"
          tabIndex={0}
          className="new-design-select-menu"
          aria-labelledby={ariaLabelledBy}
          aria-activedescendant={`${listId}-opt-${value}`}
        >
          {normalized.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li key={opt.value} role="presentation">
                <button
                  type="button"
                  id={`${listId}-opt-${opt.value}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`new-design-select-option${
                    isSelected ? ' new-design-select-option--selected' : ''
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <span className="new-design-select-option-label">{opt.label}</span>
                  <span className="new-design-select-option-meta">
                    {opt.cost != null ? (
                      <span className="new-design-select-option-cost">
                        {formatOptionCost(opt.cost)}
                      </span>
                    ) : null}
                    {isSelected ? (
                      <Check
                        className="new-design-select-check"
                        size={16}
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
